import React from "react";
import { Card, CardContent, CardHeader, Typography, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { LocalFlorist, Opacity, WbSunny, Nature } from "@material-ui/icons";
import { Line } from "react-chartjs-2";

const useStyles = makeStyles(theme => ({
  card: {
    height: "100%"
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  metricItem: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  metricIcon: {
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main
  },
  chartContainer: {
    marginTop: theme.spacing(2),
    height: "200px"
  },
  metricValue: {
    fontWeight: "bold",
    fontSize: "1.2rem"
  },
  metricLabel: {
    color: theme.palette.text.secondary
  }
}));

export default function CropHealthPanel({ data }) {
  const classes = useStyles();

  // Use actual NDVI data if available, or historical data if that's available
  const getHistoricalData = () => {
    // If we have real historical NDVI data, use it
    if (data?.ndvi?.historical_values?.length > 0) {
      return data.ndvi.historical_values.map(item => item.value);
    }
    
    // If we have historical yields, use those as an approximation
    if (data?.yieldForecast?.historical_yields?.length > 0) {
      return data.yieldForecast.historical_yields.map(item => item.value / 10); // Scale to NDVI range
    }
    
    // Last resort - use current NDVI with slight variations
    // First check for current_value (direct API format) before falling back to other formats
    const baseNDVI = data?.ndvi?.current_value || data?.ndvi?.value || 0.65;
    console.log('Using base NDVI value for historical chart:', baseNDVI);
    return Array(6).fill(0).map((_, i) => 
      baseNDVI - (0.05 * (i % 3)) + (0.03 * Math.floor(i/2))
    );
  };

  // Get labels for chart based on data source
  const getChartLabels = () => {
    // Use dates from historical NDVI if available
    if (data?.ndvi?.historical_values?.length > 0) {
      return data.ndvi.historical_values.map(item => {
        const date = new Date(item.date);
        return `${date.getMonth()+1}/${date.getDate()}`;
      });
    }
    
    // Use dates from yield forecast if available
    if (data?.yieldForecast?.historical_yields?.length > 0) {
      return data.yieldForecast.historical_yields.map(item => {
        const date = new Date(item.date);
        return `${date.getMonth()+1}/${date.getDate()}`;
      });
    }
    
    // Default labels
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  };

  const chartData = {
    labels: getChartLabels(),
    datasets: [
      {
        label: 'NDVI',
        data: getHistoricalData(),
        fill: false,
        backgroundColor: 'rgba(75,192,192,0.4)',
        borderColor: 'rgba(75,192,192,1)',
        tension: 0.1
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: false,
        min: 0.4,
        max: 0.9
      }
    },
    plugins: {
      legend: {
        display: false
      }
    }
  };

  // Extract NDVI value from API response data format
  const getNdviValue = () => {
    if (!data?.ndvi) {
      console.log('No NDVI data available');
      return "0.65";
    }
    
    console.log('NDVI data received:', data.ndvi);
    
    // Handle API format: data.ndvi = { current_value: 0.68, ... }
    if (data.ndvi.current_value !== undefined) {
      const value = data.ndvi.current_value;
      console.log('Using current_value from API:', value);
      return typeof value === 'number' ? value.toFixed(2) : value;
    }
    
    // Handle API format: data.ndvi = { value: 0.68, ... }
    if (data.ndvi.value !== undefined) {
      const value = data.ndvi.value;
      console.log('Using value from API:', value);
      return typeof value === 'number' ? value.toFixed(2) : value;
    }
    
    // Handle API format: data.ndvi is a direct number
    if (typeof data.ndvi === 'number') {
      console.log('Using direct number value:', data.ndvi);
      return data.ndvi.toFixed(2);
    }
    
    console.log('Falling back to default NDVI value');
    return "0.65";
  };
  
  // Extract yield forecast from API response
  const getYieldValue = () => {
    if (!data?.yieldForecast) return "3.5";
    
    // Handle API format: data.yieldForecast = { yield_value: 3.98, ... }
    if (data.yieldForecast.yield_value !== undefined) {
      return data.yieldForecast.yield_value.toFixed(2);
    }
    
    // Handle API format: data.yieldForecast = { predicted: 3.98, ... }
    if (data.yieldForecast.predicted !== undefined) {
      return data.yieldForecast.predicted.toFixed(2);
    }
    
    return "3.5";
  };
  
  const metrics = [
    {
      icon: <LocalFlorist />,
      label: "NDVI Index",
      value: getNdviValue(),
      suffix: ""
    },
    {
      icon: <WbSunny />,
      label: "Yield Forecast",
      value: getYieldValue(),
      suffix: data?.yieldForecast?.yield_unit ? data.yieldForecast.yield_unit : "tons/ha"
    },
    {
      icon: <Nature />,
      label: "Vegetation Index",
      value: (typeof data?.vegetationIndex === 'object' ? 
              String(data?.vegetationIndex?.value || "80") : 
              (typeof data?.vegetationIndex === 'number' ? 
                data.vegetationIndex.toFixed(2) : 
                "80")),
      suffix: "%"
    },
    {
      icon: <Opacity />,
      label: "Soil Moisture",
      value: (typeof data?.soilMoisture === 'object' ? 
              String(data?.soilMoisture?.value || "65") : 
              (typeof data?.soilMoisture === 'number' ? 
                data.soilMoisture.toFixed(2) : 
                "65")),
      suffix: "%"
    }
  ];

  return (
    <Card className={classes.card}>
      <CardHeader
        title={`Crop Health - ${data?.crop || 'Unknown Crop'}`}
        className={classes.cardHeader}
      />
      <CardContent>
        <Grid container spacing={2}>
          {metrics.map((metric, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <div className={classes.metricItem}>
                <div className={classes.metricIcon}>
                  {metric.icon}
                </div>
                <div>
                  <Typography variant="body2" className={classes.metricLabel}>
                    {metric.label}
                  </Typography>
                  <Typography variant="h6" className={classes.metricValue}>
                    {metric.value}{metric.suffix}
                  </Typography>
                </div>
              </div>
            </Grid>
          ))}
        </Grid>
        <div className={classes.chartContainer}>
          <Line data={chartData} options={chartOptions} />
        </div>
      </CardContent>
    </Card>
  );
}
